import React, { useEffect, useState, useCallback } from "react";
import { RefreshCw } from "@/components/icons";
import api from "@/lib/api";

// Renders stored media by streaming the bytes from our backend (auth-gated) and
// showing them via object URLs. Media never lands in MongoDB; only references
// (object keys) are stored on the fault/repair.
// State per key: undefined = loading, null = failed (with retry), string = ready.
export const MediaGallery = ({ media = [] }) => {
  const [urls, setUrls] = useState({});

  const fetchOne = useCallback(async (m) => {
    setUrls((prev) => ({ ...prev, [m.key]: undefined })); // loading
    try {
      const { data } = await api.get(`/storage/file?key=${encodeURIComponent(m.key)}`, {
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(data);
      setUrls((prev) => {
        const old = prev[m.key];
        if (typeof old === "string") URL.revokeObjectURL(old);
        return { ...prev, [m.key]: blobUrl };
      });
    } catch (e) {
      setUrls((prev) => ({ ...prev, [m.key]: null })); // failed -> show retry
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      for (const m of media) {
        if (!alive) break;
        await fetchOne(m);
      }
    })();
    return () => {
      alive = false;
      setUrls((prev) => {
        Object.values(prev).forEach((u) => typeof u === "string" && URL.revokeObjectURL(u));
        return {};
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media]);

  if (!media.length) return null;
  return (
    <div className="flex flex-wrap gap-3" data-testid="media-gallery">
      {media.map((m) => {
        const url = urls[m.key];
        if (url === undefined) {
          return (
            <div key={m.key} data-testid={`media-loading-${m.key}`} className="flex h-24 w-24 items-center justify-center rounded-md border border-border bg-muted/40 text-data text-muted-foreground">
              laden…
            </div>
          );
        }
        if (url === null) {
          return (
            <button
              key={m.key}
              type="button"
              data-testid={`media-retry-${m.key}`}
              onClick={() => fetchOne(m)}
              className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-md border border-border bg-muted/40 p-1 text-center text-data text-muted-foreground transition-colors duration-150 hover:bg-muted"
            >
              <RefreshCw size={18} />
              <span>Niet geladen — opnieuw</span>
            </button>
          );
        }
        return m.kind === "video" ? (
          <video key={m.key} src={url} controls className="h-40 rounded-md border border-border" data-testid={`media-video-${m.key}`} />
        ) : (
          <a key={m.key} href={url} target="_blank" rel="noreferrer">
            <img src={url} alt="media" className="h-24 w-24 rounded-md border border-border object-cover" data-testid={`media-image-${m.key}`} />
          </a>
        );
      })}
    </div>
  );
};
